import ApiService from "service/Api";
import {
  QuestUnitPayloadTypes,
  QuestMissionPayloadTypes,
  QuestCheckpointPayloadTypes,
  QuestBadgePayloadTypes,
} from "utilities/types/Quest";

const normalizePayload = (payload: any) => {
  if (!payload || typeof payload !== "object") return payload;
  const clean = { ...payload };
  if ("is_active" in clean && clean.is_active !== undefined) {
    clean.is_active =
      clean.is_active === true ||
      clean.is_active === "true" ||
      clean.is_active === 1 ||
      clean.is_active === "1"
        ? 1
        : 0;
  }
  return clean;
};

const buildRequestData = (url: string, payload: any, isUpdate = false) => {
  const cleanPayload = normalizePayload(payload);
  const hasFile = Object.values(cleanPayload || {}).some(
    (v) => v instanceof File || (Array.isArray(v) && v.some((item) => item instanceof File))
  );

  if (hasFile) {
    const formData = new FormData();
    Object.keys(cleanPayload).forEach((key) => {
      const val = cleanPayload[key];
      if (val !== undefined && val !== null) {
        if (val instanceof File) {
          formData.append(key, val);
        } else if (typeof val === "boolean" || key === "is_active") {
          formData.append(
            key,
            val === true || val === "true" || val === 1 || val === "1" ? "1" : "0"
          );
        } else if (typeof val === "object" && !(val instanceof Blob)) {
          formData.append(key, JSON.stringify(val));
        } else {
          formData.append(key, String(val));
        }
      }
    });

    if (isUpdate) {
      formData.append("_method", "PUT");
    }

    return {
      method: "post",
      headers: { "Content-Type": "multipart/form-data; charset=utf-8" },
      data: formData,
      url,
    };
  }

  return {
    method: isUpdate ? "put" : "post",
    headers: { "Content-Type": "application/json; charset=utf-8" },
    data: cleanPayload,
    url,
  };
};

const QuestService = {
  // ─── Units ─────────────────────────────────────────────────────────────────

  getAllUnits: async (params?: { is_active?: boolean; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.is_active !== undefined)
      query.append("is_active", String(params.is_active));
    if (params?.search) query.append("search", params.search);

    const requestData = {
      method: "get",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      url: `/admin/quests/units${query.toString() ? "?" + query.toString() : ""}`,
    };
    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  storeUnit: async (payload: QuestUnitPayloadTypes) => {
    const requestData = {
      method: "post",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      data: normalizePayload(payload),
      url: "/admin/quests/units",
    };
    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  updateUnit: async (unitId: string, payload: QuestUnitPayloadTypes) => {
    const requestData = {
      method: "put",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      data: normalizePayload(payload),
      url: `/admin/quests/units/${unitId}`,
    };
    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  deleteUnit: async (unitId: string) => {
    const requestData = {
      method: "delete",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      url: `/admin/quests/units/${unitId}`,
    };
    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ─── Missions ──────────────────────────────────────────────────────────────

  getAllMissions: async (params?: {
    master_unit_id?: string | number;
    is_active?: boolean;
  }) => {
    const query = new URLSearchParams();
    if (params?.master_unit_id !== undefined)
      query.append("master_unit_id", String(params.master_unit_id));
    if (params?.is_active !== undefined)
      query.append("is_active", String(params.is_active));

    const requestData = {
      method: "get",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      url: `/admin/quests/missions${query.toString() ? "?" + query.toString() : ""}`,
    };
    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  storeMission: async (payload: QuestMissionPayloadTypes) => {
    const requestData = buildRequestData("/admin/quests/missions", payload, false);
    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  updateMission: async (
    missionId: string,
    payload: QuestMissionPayloadTypes
  ) => {
    const requestData = buildRequestData(
      `/admin/quests/missions/${missionId}`,
      payload,
      true
    );
    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  deleteMission: async (missionId: string) => {
    const requestData = {
      method: "delete",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      url: `/admin/quests/missions/${missionId}`,
    };
    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ─── Checkpoints ───────────────────────────────────────────────────────────

  getCheckpoints: async (missionId: string) => {
    const requestData = {
      method: "get",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      url: `/admin/quests/missions/${missionId}/checkpoints`,
    };
    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  storeCheckpoint: async (
    missionId: string,
    payload: QuestCheckpointPayloadTypes
  ) => {
    const requestData = {
      method: "post",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      data: normalizePayload(payload),
      url: `/admin/quests/missions/${missionId}/checkpoints`,
    };
    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  updateCheckpoint: async (
    checkpointId: string,
    payload: QuestCheckpointPayloadTypes
  ) => {
    const requestData = {
      method: "put",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      data: normalizePayload(payload),
      url: `/admin/quests/checkpoints/${checkpointId}`,
    };
    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  deleteCheckpoint: async (checkpointId: string) => {
    const requestData = {
      method: "delete",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      url: `/admin/quests/checkpoints/${checkpointId}`,
    };
    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  // ─── Badge Tiers ───────────────────────────────────────────────────────────

  getBadges: async (missionId: string) => {
    const requestData = {
      method: "get",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      url: `/admin/quests/missions/${missionId}/badges`,
    };
    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  storeBadge: async (missionId: string, payload: QuestBadgePayloadTypes) => {
    const requestData = buildRequestData(
      `/admin/quests/missions/${missionId}/badges`,
      payload,
      false
    );
    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  updateBadge: async (badgeId: string, payload: QuestBadgePayloadTypes) => {
    const requestData = buildRequestData(
      `/admin/quests/badges/${badgeId}`,
      payload,
      true
    );
    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  deleteBadge: async (badgeId: string) => {
    const requestData = {
      method: "delete",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      url: `/admin/quests/badges/${badgeId}`,
    };
    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data;
    } catch (error) {
      throw error;
    }
  },
};

export default QuestService;
